import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import type { KitchenSnapshot, StationId } from '../game/types';
import { STATION_LABELS } from '../game/types';

const COLORS = {
  cream: 0xfff2df,
  creamDark: 0xe4cdb1,
  teal: 0x08a9a5,
  tealDark: 0x087b7c,
  coral: 0xe94338,
  coralDark: 0xa92324,
  gold: 0xf3bd55,
  steel: 0xbfc9cc,
  steelDark: 0x536269,
  wood: 0xc9824d,
  woodDark: 0x6b3e2b,
  rice: 0xffda73,
  sauce: 0xb52b1e,
  green: 0x6ca83b,
};

export interface KitchenWorldDiagnostics {
  stationCount: number;
  meshes: number;
  materials: number;
  geometries: number;
}

interface VisualRefs {
  chickenCrateOnBoard: THREE.Group;
  rawChicken: THREE.Group;
  cutChicken: THREE.Group;
  marinadeChicken: THREE.Group;
  fridgeChicken: THREE.Group;
  riceBagAtSink: THREE.Group;
  riceBowl: THREE.Group;
  riceWater: THREE.Mesh;
  vegetables: THREE.Group;
  vegetableCuts: THREE.Group;
  ovenDoor: THREE.Group;
  ovenNeedle: THREE.Group;
  ovenBody: THREE.Group;
  ovenGlow: THREE.Mesh;
  ovenSteam: THREE.Group;
  platter: THREE.Group;
  courierArm: THREE.Group;
}

interface TimerBubble {
  sprite: THREE.Sprite;
  canvas: HTMLCanvasElement;
  context: CanvasRenderingContext2D;
  texture: THREE.CanvasTexture;
  lastText: string;
}

const ASSET_PATHS = {
  chicken: './assets/ingredients/chicken-packaging-sheet.png',
  rice: './assets/ingredients/rice-sacks-sheet.png',
  onion: './assets/ingredients/onion-sheet.png',
  tomato: './assets/ingredients/tomato-sheet.png',
  potato: './assets/ingredients/potato-sheet.png',
  logo: './assets/brand/mandy-kaif-logo.jpg',
} as const;

export class KitchenWorld {
  readonly root = new THREE.Group();
  readonly interactables: THREE.Object3D[] = [];
  readonly stations = new Map<StationId, THREE.Group>();
  readonly diagnostics: KitchenWorldDiagnostics;

  private readonly rings = new Map<StationId, THREE.Mesh>();
  private readonly labels = new Map<StationId, THREE.Sprite>();
  private readonly timerBubbles = new Map<StationId, TimerBubble>();
  private readonly attentionArrows = new Map<StationId, THREE.Sprite>();
  private attentionArrowMaterial?: THREE.SpriteMaterial;
  private readonly refs: VisualRefs;
  private readonly chef: THREE.Group;
  private readonly chefTarget = new THREE.Vector3(3.65, 0, 3.25);
  private chefMoving = false;
  private pulse: { id: StationId; elapsed: number } | null = null;
  private lastChickenCutHalves = 0;
  private chickenCutVisualRemaining = 0;

  constructor() {
    this.root.name = 'MandyKaifKitchen';
    this.root.add(this.createEnvironment());

    const chickenCrates = this.createChickenCrates();
    const chickenBoard = this.createChickenBoard();
    const marinade = this.createMarinadeStation();
    const fridge = this.createFridge();
    const riceBags = this.createRiceBags();
    const sink = this.createSink();
    const vegetables = this.createVegetableBasket();
    const vegBoard = this.createVegetableBoard();
    const oven = this.createOven();
    const serve = this.createCourier();

    // One connected U-shaped production line. The wings deliberately turn
    // toward the aisle so the route reads as one kitchen instead of islands.
    this.addStation('chicken-crates', chickenCrates.station, [-6.55, 0, -3.78]);
    this.addStation('chicken-board', chickenBoard.station, [-4.5, 0, -3.78]);
    this.addStation('marinade', marinade.station, [-2.42, 0, -3.78]);
    this.addStation('fridge', fridge.station, [-0.15, 0, -3.72]);
    this.addStation('oven', oven.station, [3.55, 0, -3.55]);
    riceBags.station.rotation.y = Math.PI / 2;
    sink.station.rotation.y = Math.PI / 2;
    this.addStation('rice-bags', riceBags.station, [-7.25, 0, -1.08]);
    this.addStation('sink', sink.station, [-7.25, 0, 1.28]);
    vegetables.station.rotation.y = Math.PI;
    vegBoard.station.rotation.y = Math.PI;
    this.addStation('veg-basket', vegetables.station, [-5.9, 0, 4.65]);
    this.addStation('veg-board', vegBoard.station, [-3.25, 0, 4.65]);
    this.addStation('serve', serve.station, [5.75, 0, 4.85]);

    this.chef = this.createChef();
    this.chef.position.copy(this.chefTarget);
    this.root.add(this.chef);

    this.refs = {
      chickenCrateOnBoard: chickenBoard.crate,
      rawChicken: chickenBoard.rawChicken,
      cutChicken: chickenBoard.cutChicken,
      marinadeChicken: marinade.chicken,
      fridgeChicken: fridge.chicken,
      riceBagAtSink: sink.riceBag,
      riceBowl: sink.riceBowl,
      riceWater: sink.water,
      vegetables: vegBoard.vegetables,
      vegetableCuts: vegBoard.cuts,
      ovenDoor: oven.door,
      ovenNeedle: oven.needle,
      ovenBody: oven.body,
      ovenGlow: oven.glow,
      ovenSteam: oven.steam,
      platter: oven.platter,
      courierArm: serve.arm,
    };

    this.installTimerBubble('chicken-board', 4.05);
    this.installTimerBubble('marinade', 4.05);
    this.installTimerBubble('fridge', 5.05);
    this.installTimerBubble('sink', 4.05);
    this.installTimerBubble('veg-board', 4.05);
    this.installTimerBubble('oven', 5.05);

    this.diagnostics = this.measure();
  }

  pulseStation(id: StationId): void {
    this.pulse = { id, elapsed: 0 };
    const station = this.stations.get(id);
    if (!station) return;
    station.scale.setScalar(0.92);
    const approach = new THREE.Vector3(-station.position.x, 0, -station.position.z).normalize();
    const distance = id === 'oven' ? 2.05 : id === 'serve' ? 1.65 : 1.35;
    this.chefTarget.copy(station.position).addScaledVector(approach, distance);
    this.chefMoving = true;
  }

  update(snapshot: KitchenSnapshot, delta: number, elapsed: number, reducedMotion = false): void {
    const t = reducedMotion ? 0 : elapsed;
    const chefDelta = this.chefTarget.clone().sub(this.chef.position);
    const chefDistance = chefDelta.length();
    if (chefDistance > 0.06) {
      const travel = Math.min(chefDistance, delta * 7.2);
      chefDelta.normalize();
      this.chef.position.addScaledVector(chefDelta, travel);
      this.chef.rotation.y = Math.atan2(chefDelta.x, chefDelta.z);
      this.chefMoving = true;
    } else {
      this.chefMoving = false;
    }
    this.chef.position.y = this.chefMoving && !reducedMotion ? Math.abs(Math.sin(t * 12)) * 0.07 : 0;
    const leftArm = this.chef.getObjectByName('chef-left-arm');
    const rightArm = this.chef.getObjectByName('chef-right-arm');
    if (leftArm && rightArm) {
      const swing = this.chefMoving && !reducedMotion ? Math.sin(t * 12) * 0.62 : this.pulse ? Math.sin(t * 18) * 0.28 - 0.45 : -0.12;
      leftArm.rotation.x = swing;
      rightArm.rotation.x = -swing;
    }
    // Desktop keeps every world caption visible. On narrow screens the labeled
    // station dock already carries the same names, so hiding duplicate world
    // captions saves draw calls and keeps the kitchen legible.
    const showPersistentWorldLabels = window.innerWidth >= 700;
    for (const [id, ring] of this.rings) {
      const attention = snapshot.attentionStations.includes(id);
      const active = id === snapshot.nextStation || attention;
      ring.visible = active;
      const scale = active ? 1 + Math.sin(t * (attention ? 7.5 : 4.5)) * (attention ? 0.12 : 0.06) : 1;
      ring.scale.setScalar(scale);
      const material = ring.material as THREE.MeshStandardMaterial;
      const signalColor = attention ? COLORS.coral : COLORS.teal;
      material.color.setHex(signalColor);
      material.emissive.setHex(signalColor);
      material.emissiveIntensity = active ? (attention ? 3.4 : 2.2) + Math.sin(t * 5) * 0.45 : 0.25;
      material.opacity = active ? 0.94 : 0.34;
      const label = this.labels.get(id);
      if (label) label.visible = id !== 'serve' && showPersistentWorldLabels;
      const arrow = this.attentionArrows.get(id);
      if (arrow) {
        arrow.visible = attention;
        if (attention) {
          const baseY = Number(arrow.userData.baseY ?? arrow.position.y);
          arrow.position.y = baseY + (reducedMotion ? 0 : Math.sin(t * 7.5) * 0.2);
          const arrowPulse = reducedMotion ? 1 : 1 + Math.sin(t * 7.5) * 0.08;
          arrow.scale.set(1.08 * arrowPulse, 1.55 * arrowPulse, 1);
        }
      }
    }

    if (snapshot.chicken.cutHalves !== this.lastChickenCutHalves) {
      this.lastChickenCutHalves = snapshot.chicken.cutHalves;
      this.chickenCutVisualRemaining = snapshot.chicken.cutHalves > 0 && snapshot.chicken.cutHalves < 2 ? 1 : 0;
    }
    this.chickenCutVisualRemaining = Math.max(0, this.chickenCutVisualRemaining - delta);
    const chickenCutActive = snapshot.chicken.onBoard > 0 && snapshot.chicken.cutHalves < 2;
    this.setTimerBubble(
      'chicken-board',
      chickenCutActive,
      'تقطيع الدجاج',
      this.chickenCutVisualRemaining > 0 ? `${Math.max(1, Math.ceil(this.chickenCutVisualRemaining))}` : `${2 - snapshot.chicken.cutHalves}`,
      this.chickenCutVisualRemaining > 0 ? 'ثانية متبقية' : 'خطوة متبقية',
      t,
    );
    this.setTimerBubble(
      'marinade',
      snapshot.chicken.marinadeRemaining > 0,
      'التتبيلة',
      `${Math.ceil(snapshot.chicken.marinadeRemaining)}`,
      'ثانية متبقية',
      t,
    );
    this.setTimerBubble(
      'fridge',
      snapshot.chicken.chillRemaining > 0,
      'التبريد',
      `${Math.ceil(snapshot.chicken.chillRemaining)}`,
      'ثانية متبقية',
      t,
    );
    this.setTimerBubble(
      'sink',
      snapshot.rice.washCooldown > 0,
      'غسل الرز',
      `${Math.max(1, Math.ceil(snapshot.rice.washCooldown))}`,
      `ثانية • الغسلة ${snapshot.rice.washCount} من 3`,
      t,
    );
    this.setTimerBubble(
      'veg-board',
      snapshot.vegetables.chopCooldown > 0,
      'تقطيع الخضروات',
      `${Math.max(1, Math.ceil(snapshot.vegetables.chopCooldown))}`,
      `ثانية • التقطيعة ${snapshot.vegetables.chopCount} من 3`,
      t,
    );
    this.setTimerBubble(
      'oven',
      snapshot.oven.cooking,
      'فرن المندي',
      `${Math.max(0, Math.ceil(snapshot.oven.remaining))}`,
      'ثانية متبقية',
      t,
    );

    if (this.pulse) {
      this.pulse.elapsed += delta;
      const k = Math.min(this.pulse.elapsed / 0.26, 1);
      const s = 0.92 + Math.sin(k * Math.PI) * 0.16 + k * 0.08;
      this.stations.get(this.pulse.id)?.scale.setScalar(s);
      if (k >= 1) {
        this.stations.get(this.pulse.id)?.scale.setScalar(1);
        this.pulse = null;
      }
    }

    const chicken = snapshot.chicken;
    this.refs.chickenCrateOnBoard.visible = chicken.cratesTaken > 0 && chicken.onBoard === 0;
    this.refs.rawChicken.visible = chicken.onBoard > 0;
    this.refs.cutChicken.visible = chicken.cutHalves >= 2;
    this.refs.marinadeChicken.visible = chicken.marinadeCount > 0;
    this.refs.fridgeChicken.visible = chicken.chilledCount > 0;

    this.refs.riceBagAtSink.visible = snapshot.rice.bagPlaced;
    this.refs.riceBowl.visible = snapshot.rice.bowlFilled && !snapshot.rice.ovenReady;
    this.refs.riceWater.visible = snapshot.rice.bowlFilled && snapshot.rice.washCount > 0 && snapshot.rice.washCooldown > 0;
    const waterMat = this.refs.riceWater.material as THREE.MeshPhysicalMaterial;
    waterMat.opacity = this.refs.riceWater.visible ? 0.48 : 0;

    this.refs.vegetables.visible = snapshot.vegetables.revealed && !snapshot.vegetables.ovenReady;
    this.refs.vegetableCuts.visible = snapshot.vegetables.chopCount > 0 && !snapshot.vegetables.ovenReady;
    for (let i = 0; i < this.refs.vegetableCuts.children.length; i += 1) {
      this.refs.vegetableCuts.children[i].visible = i < snapshot.vegetables.chopCount;
    }

    const doorTarget = snapshot.oven.cooking ? 0 : snapshot.oven.platterReady ? -1.48 : -0.72;
    this.refs.ovenDoor.rotation.y = THREE.MathUtils.lerp(this.refs.ovenDoor.rotation.y, doorTarget, 1 - Math.exp(-delta * 5));
    const needleTarget = snapshot.oven.cooking ? -0.62 + Math.sin(t * 2.2) * 0.1 : -1.15;
    this.refs.ovenNeedle.rotation.z = THREE.MathUtils.lerp(this.refs.ovenNeedle.rotation.z, needleTarget, 1 - Math.exp(-delta * 3.2));
    this.refs.ovenBody.position.y = snapshot.oven.cooking && !reducedMotion ? Math.sin(t * 18) * 0.012 : 0;
    const glowMat = this.refs.ovenGlow.material as THREE.MeshStandardMaterial;
    glowMat.emissiveIntensity = snapshot.oven.cooking ? 2.8 + Math.sin(t * 7) * 0.6 : 0.12;
    glowMat.opacity = snapshot.oven.cooking ? 0.9 : 0.25;
    this.refs.ovenSteam.visible = snapshot.oven.cooking || snapshot.oven.platterReady;
    this.refs.ovenSteam.children.forEach((steam, index) => {
      steam.position.y = 5.45 + ((t * (0.48 + index * 0.06) + index * 0.52) % 1.7);
      steam.position.x = Math.sin(t * 1.4 + index) * 0.22;
      const m = (steam as THREE.Mesh).material as THREE.MeshBasicMaterial;
      m.opacity = 0.26 * (1 - (steam.position.y - 5.45) / 1.7);
    });
    this.refs.platter.visible = snapshot.oven.platterReady;
    if (snapshot.oven.platterReady) {
      this.refs.platter.rotation.y = t * 0.22;
      this.refs.platter.position.y = 1.45 + Math.sin(t * 2.6) * 0.06;
    }

    const armTarget = snapshot.order.status === 'ready' ? -0.55 : 0.15;
    this.refs.courierArm.rotation.x = THREE.MathUtils.lerp(this.refs.courierArm.rotation.x, armTarget, 1 - Math.exp(-delta * 4));
  }

  private installTimerBubble(id: StationId, height: number): void {
    const station = this.stations.get(id);
    if (!station) return;
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 360;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Canvas 2D unavailable for timer bubbles.');
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: texture, transparent: true, depthWrite: false, depthTest: false }));
    sprite.position.set(0, height, 0.12);
    sprite.scale.set(3.45, 2.42, 1);
    sprite.renderOrder = 30;
    sprite.visible = false;
    station.add(sprite);
    this.timerBubbles.set(id, { sprite, canvas, context, texture, lastText: '' });
  }

  private setTimerBubble(id: StationId, visible: boolean, title: string, value: string, meta: string, elapsed: number): void {
    const bubble = this.timerBubbles.get(id);
    if (!bubble) return;
    bubble.sprite.visible = visible;
    if (!visible) return;
    // The countdown replaces the regular station caption while active; keeping
    // both produces a double stack that is hard to scan from the game camera.
    const stationLabel = this.labels.get(id);
    if (stationLabel) stationLabel.visible = false;
    const signature = `${title}|${value}|${meta}`;
    if (bubble.lastText !== signature) {
      const { context: ctx, canvas } = bubble;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.shadowColor = 'rgba(55, 35, 24, 0.34)';
      ctx.shadowBlur = 22;
      ctx.shadowOffsetY = 12;
      ctx.fillStyle = 'rgba(255, 248, 234, 0.99)';
      ctx.strokeStyle = '#e84338';
      ctx.lineWidth = 10;
      ctx.beginPath();
      ctx.roundRect(20, 14, 472, 286, 54);
      ctx.fill();
      ctx.stroke();
      ctx.shadowColor = 'transparent';
      ctx.fillStyle = '#0a8582';
      ctx.beginPath();
      ctx.roundRect(42, 38, 428, 70, 28);
      ctx.fill();
      ctx.fillStyle = '#fff8ea';
      ctx.font = '800 38px Tahoma, Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.direction = 'rtl';
      ctx.fillText(title, 256, 74);
      ctx.fillStyle = '#e73832';
      ctx.font = value.length > 2 ? '900 100px Tahoma, Arial, sans-serif' : '900 124px Tahoma, Arial, sans-serif';
      ctx.fillText(value, 256, 190);
      ctx.fillStyle = '#243f3f';
      ctx.font = '800 30px Tahoma, Arial, sans-serif';
      ctx.fillText(meta, 256, 262);
      ctx.fillStyle = '#e84338';
      ctx.beginPath();
      ctx.moveTo(222, 298);
      ctx.lineTo(290, 298);
      ctx.lineTo(256, 350);
      ctx.closePath();
      ctx.fill();
      bubble.texture.needsUpdate = true;
      bubble.lastText = signature;
    }
    const pulse = 1 + Math.sin(elapsed * 4.2) * 0.025;
    bubble.sprite.scale.set(3.45 * pulse, 2.42 * pulse, 1);
  }

  private addStation(id: StationId, station: THREE.Group, position: [number, number, number]): void {
    station.name = `station-${id}`;
    station.position.set(...position);
    station.userData.stationId = id;
    station.traverse((node) => {
      node.userData.stationId = id;
      if (node instanceof THREE.Mesh) {
        node.castShadow = true;
        node.receiveShadow = true;
        this.interactables.push(node);
      }
    });

    const ring = new THREE.Mesh(
      new THREE.RingGeometry(1.05, 1.16, 48),
      new THREE.MeshStandardMaterial({
        color: COLORS.teal,
        emissive: COLORS.teal,
        emissiveIntensity: 0.35,
        transparent: true,
        opacity: 0.34,
        depthWrite: false,
        side: THREE.DoubleSide,
      }),
    );
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.028;
    ring.userData.stationId = id;
    station.add(ring);
    this.rings.set(id, ring);

    const bounds = new THREE.Box3().setFromObject(station);
    const label = this.createLabel(STATION_LABELS[id], 320, 96, COLORS.tealDark, 38);
    const labelY = THREE.MathUtils.clamp(bounds.max.y + 0.34, 2.38, 5.65);
    label.position.set(0, labelY, 0);
    label.scale.set(1.55, 0.46, 1);
    label.visible = id !== 'serve';
    label.userData.stationId = id;
    station.add(label);
    this.labels.set(id, label);

    const arrow = this.createAttentionArrow();
    arrow.position.set(0, labelY + 1.02, 0.18);
    arrow.userData.baseY = arrow.position.y;
    arrow.userData.stationId = id;
    station.add(arrow);
    this.attentionArrows.set(id, arrow);

    this.stations.set(id, station);
    this.root.add(station);
  }

  private createAttentionArrow(): THREE.Sprite {
    if (!this.attentionArrowMaterial) {
      const canvas = document.createElement('canvas');
      canvas.width = 180;
      canvas.height = 240;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas 2D unavailable for attention arrows.');
      ctx.shadowColor = 'rgba(80, 0, 0, 0.45)';
      ctx.shadowBlur = 18;
      ctx.fillStyle = '#e73832';
      ctx.strokeStyle = '#fff8ea';
      ctx.lineWidth = 9;
      ctx.beginPath();
      ctx.roundRect(64, 18, 52, 118, 22);
      ctx.moveTo(28, 116);
      ctx.lineTo(152, 116);
      ctx.lineTo(90, 220);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      const texture = new THREE.CanvasTexture(canvas);
      texture.colorSpace = THREE.SRGBColorSpace;
      this.attentionArrowMaterial = new THREE.SpriteMaterial({ map: texture, transparent: true, depthWrite: false, depthTest: false });
    }
    const arrow = new THREE.Sprite(this.attentionArrowMaterial);
    arrow.scale.set(1.08, 1.55, 1);
    arrow.renderOrder = 40;
    arrow.visible = false;
    return arrow;
  }

  private createEnvironment(): THREE.Group {
    const group = new THREE.Group();
    const floor = new THREE.Mesh(new THREE.PlaneGeometry(19.2, 16.4), new THREE.MeshStandardMaterial({ color: 0x526b6d, roughness: 0.8 }));
    floor.rotation.x = -Math.PI / 2;
    floor.position.z = 2.2;
    floor.receiveShadow = true;
    group.add(floor);

    // Bold alternating floor makes the cooking lanes legible from the elevated camera.
    const tileGeo = new THREE.BoxGeometry(1.15, 0.08, 1.15);
    const tileA = new THREE.InstancedMesh(tileGeo, new THREE.MeshStandardMaterial({ color: 0xe8cfaa, roughness: 0.76 }), 105);
    const tileB = new THREE.InstancedMesh(tileGeo, new THREE.MeshStandardMaterial({ color: 0xa4cfca, roughness: 0.76 }), 105);
    const matrix = new THREE.Matrix4();
    let ai = 0;
    let bi = 0;
    for (let row = 0; row < 13; row += 1) {
      for (let column = 0; column < 15; column += 1) {
        matrix.makeTranslation(-8.05 + column * 1.15, 0.04, -4.6 + row * 1.15);
        const target = (row + column) % 2 ? tileA : tileB;
        target.setMatrixAt((row + column) % 2 ? ai++ : bi++, matrix);
      }
    }
    tileA.count = ai;
    tileB.count = bi;
    tileA.instanceMatrix.needsUpdate = true;
    tileB.instanceMatrix.needsUpdate = true;
    tileA.receiveShadow = true;
    tileB.receiveShadow = true;
    group.add(tileA, tileB);

    const wallMat = new THREE.MeshStandardMaterial({ color: 0xfff2df, roughness: 0.66 });
    const backWall = new THREE.Mesh(new RoundedBoxGeometry(18.5, 5.9, 0.4, 4, 0.12), wallMat);
    backWall.position.set(0, 2.95, -5.48);
    backWall.receiveShadow = true;
    group.add(backWall);

    const leftWall = new THREE.Mesh(new RoundedBoxGeometry(0.4, 3.0, 14.8, 4, 0.12), wallMat);
    leftWall.position.set(-9.05, 1.5, 1.75);
    group.add(leftWall);

    const stripe = new THREE.Mesh(
      new THREE.BoxGeometry(18.0, 0.34, 0.05),
      new THREE.MeshStandardMaterial({ color: COLORS.teal, roughness: 0.5 }),
    );
    stripe.position.set(0, 3.62, -5.25);
    group.add(stripe);

    const triangleMatA = new THREE.MeshStandardMaterial({ color: COLORS.coral, roughness: 0.55 });
    const triangleMatB = new THREE.MeshStandardMaterial({ color: COLORS.teal, roughness: 0.55 });
    const triShape = new THREE.Shape();
    triShape.moveTo(-0.18, 0.14);
    triShape.lineTo(0.18, 0.14);
    triShape.lineTo(0, -0.14);
    triShape.closePath();
    for (let i = 0; i < 27; i += 1) {
      const tri = new THREE.Mesh(new THREE.ShapeGeometry(triShape), i % 2 ? triangleMatA : triangleMatB);
      tri.position.set(-7.6 + i * 0.58, 3.62, -5.21);
      group.add(tri);
    }

    const sign = new THREE.Group();
    const plate = new THREE.Mesh(new RoundedBoxGeometry(2.58, 2.58, 0.16, 5, 0.16), new THREE.MeshStandardMaterial({ color: COLORS.cream, roughness: 0.5 }));
    sign.add(plate);
    const logoTexture = new THREE.TextureLoader().load(ASSET_PATHS.logo);
    logoTexture.colorSpace = THREE.SRGBColorSpace;
    const logo = new THREE.Mesh(
      new THREE.PlaneGeometry(2.28, 2.28),
      new THREE.MeshBasicMaterial({ map: logoTexture, toneMapped: false }),
    );
    logo.position.z = 0.095;
    sign.add(logo);
    sign.position.set(0.15, 4.48, -5.17);
    group.add(sign);

    // Continuous cabinet fillers visually connect the stations into a real kitchen line.
    const cabinetMat = new THREE.MeshStandardMaterial({ color: 0x7d4030, roughness: 0.63 });
    const topMat = new THREE.MeshStandardMaterial({ color: 0xe5a24d, roughness: 0.54 });
    const addCabinet = (x: number, z: number, width: number, depth: number): void => {
      const base = new THREE.Mesh(new RoundedBoxGeometry(width, 1.16, depth, 3, 0.06), cabinetMat);
      base.position.set(x, 0.62, z);
      group.add(base);
      const top = new THREE.Mesh(new RoundedBoxGeometry(width + 0.1, 0.18, depth + 0.12, 3, 0.05), topMat);
      top.position.set(x, 1.27, z);
      group.add(top);
      const toe = new THREE.Mesh(new THREE.BoxGeometry(width * 0.86, 0.16, 0.08), new THREE.MeshStandardMaterial({ color: 0x3e302a, roughness: 0.78 }));
      toe.position.set(x, 0.14, z + depth * 0.51);
      group.add(toe);
    };
    // Counter infill closes every visual gap between the interactive modules.
    // Together with the rotated side/front stations this reads as a single U.
    addCabinet(-1.35, -3.8, 0.34, 1.5);
    addCabinet(1.45, -3.8, 1.45, 1.5);
    addCabinet(5.25, -3.8, 1.0, 1.5);
    addCabinet(-7.25, -2.56, 1.5, 0.96);
    addCabinet(-7.25, 0.05, 1.5, 0.28);
    addCabinet(-7.25, 3.14, 1.5, 1.52);
    addCabinet(-1.02, 4.65, 2.34, 1.5);

    // Central pass-through island; it frames the route without cluttering it.
    const island = new THREE.Group();
    const islandBase = new THREE.Mesh(new RoundedBoxGeometry(2.65, 1.12, 1.12, 4, 0.08), new THREE.MeshStandardMaterial({ color: 0x225f60, roughness: 0.58 }));
    islandBase.position.y = 0.62;
    const islandTop = new THREE.Mesh(new RoundedBoxGeometry(2.88, 0.2, 1.3, 4, 0.07), topMat);
    islandTop.position.y = 1.26;
    island.add(islandBase, islandTop);
    for (const x of [-0.72, 0, 0.72]) {
      const plate = new THREE.Mesh(new THREE.CylinderGeometry(0.31, 0.34, 0.06, 24), new THREE.MeshStandardMaterial({ color: 0xf9f4e9, roughness: 0.38 }));
      plate.position.set(x, 1.4, 0);
      island.add(plate);
    }
    island.position.set(1.55, 0, 1.65);
    group.add(island);

    const hood = new THREE.Group();
    const hoodBody = new THREE.Mesh(new THREE.CylinderGeometry(0.9, 1.55, 0.78, 4), new THREE.MeshStandardMaterial({ color: 0x9ba8aa, metalness: 0.78, roughness: 0.32 }));
    hoodBody.rotation.y = Math.PI / 4;
    hoodBody.position.y = 4.35;
    const hoodPipe = new THREE.Mesh(new THREE.BoxGeometry(0.78, 1.25, 0.78), new THREE.MeshStandardMaterial({ color: 0xaab4b5, metalness: 0.8, roughness: 0.3 }));
    hoodPipe.position.y = 5.27;
    hood.add(hoodBody, hoodPipe);
    hood.scale.setScalar(0.78);
    hood.position.set(3.55, 0, -3.82);
    group.add(hood);

    // Service hatch and exterior queue hints add life beyond the playable kitchen.
    const hatch = new THREE.Mesh(new RoundedBoxGeometry(3.1, 2.0, 0.2, 4, 0.09), new THREE.MeshStandardMaterial({ color: 0x3e302a, roughness: 0.64 }));
    hatch.position.set(6.65, 2.45, -5.2);
    group.add(hatch);
    const hatchInset = new THREE.Mesh(new THREE.PlaneGeometry(2.65, 1.56), new THREE.MeshBasicMaterial({ color: 0x9fced0 }));
    hatchInset.position.set(6.65, 2.48, -5.08);
    group.add(hatchInset);
    const passTop = new THREE.Mesh(new RoundedBoxGeometry(3.35, 0.18, 0.75, 3, 0.05), topMat);
    passTop.position.set(6.65, 1.36, -4.94);
    group.add(passTop);

    for (const z of [-3.25, -1.3, 0.65, 2.6, 4.55, 6.5]) {
      const bumper = new THREE.Mesh(new RoundedBoxGeometry(0.2, 0.75, 1.2, 3, 0.05), new THREE.MeshStandardMaterial({ color: COLORS.coralDark, roughness: 0.62 }));
      bumper.position.set(-8.82, 0.55, z);
      group.add(bumper);
    }

    const plantMat = new THREE.MeshStandardMaterial({ color: COLORS.green, roughness: 0.75 });
    for (const x of [-8.35, 8.35]) {
      const pot = new THREE.Mesh(new THREE.CylinderGeometry(0.42, 0.52, 0.82, 16), new THREE.MeshStandardMaterial({ color: COLORS.coralDark, roughness: 0.7 }));
      pot.position.set(x, 0.42, 8.0);
      group.add(pot);
      for (let i = 0; i < 5; i += 1) {
        const leaf = new THREE.Mesh(new THREE.SphereGeometry(0.33, 14, 10), plantMat);
        leaf.scale.set(0.6, 1.5, 0.35);
        leaf.rotation.z = (i - 2) * 0.34;
        leaf.position.set(x + (i - 2) * 0.15, 1.12 + Math.abs(i - 2) * 0.05, 8.0);
        group.add(leaf);
      }
    }

    // Exterior street layer fills the previously empty screen edges. It lives
    // outside the kitchen footprint, so it adds context without stealing clicks.
    const asphalt = new THREE.MeshStandardMaterial({ color: 0x39464b, roughness: 0.94 });
    const roadRight = new THREE.Mesh(new THREE.PlaneGeometry(9.5, 24), asphalt);
    roadRight.rotation.x = -Math.PI / 2;
    roadRight.position.set(12.5, -0.035, 3.0);
    roadRight.receiveShadow = true;
    group.add(roadRight);
    const roadFront = new THREE.Mesh(new THREE.PlaneGeometry(36, 8.2), asphalt);
    roadFront.rotation.x = -Math.PI / 2;
    roadFront.position.set(0, -0.04, 14.2);
    roadFront.receiveShadow = true;
    group.add(roadFront);

    const sidewalkMat = new THREE.MeshStandardMaterial({ color: 0xd7c7ad, roughness: 0.88 });
    const sidewalkRight = new THREE.Mesh(new THREE.BoxGeometry(1.3, 0.22, 22), sidewalkMat);
    sidewalkRight.position.set(8.85, 0.06, 3.0);
    group.add(sidewalkRight);
    const sidewalkFront = new THREE.Mesh(new THREE.BoxGeometry(20.2, 0.22, 1.3), sidewalkMat);
    sidewalkFront.position.set(0, 0.06, 10.75);
    group.add(sidewalkFront);

    const roadMarkMat = new THREE.MeshBasicMaterial({ color: 0xffe7a0 });
    for (const z of [-5, -1.5, 2, 5.5, 9, 12.5]) {
      const mark = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.03, 1.9), roadMarkMat);
      mark.position.set(11.7, 0.015, z);
      group.add(mark);
    }
    for (let i = 0; i < 6; i += 1) {
      const crossing = new THREE.Mesh(new THREE.BoxGeometry(1.2, 0.035, 0.34), new THREE.MeshBasicMaterial({ color: 0xf7efe1 }));
      crossing.position.set(8.6 + i * 1.5, 0.018, 11.65);
      group.add(crossing);
    }

    const shopColors = [0xd36449, 0x358f8b, 0xcf963f];
    const shopNames = ['بقالة الحارة', 'مخبز التنور', 'قهوة عربية'];
    for (let i = 0; i < 3; i += 1) {
      const z = -3.15 + i * 5.35;
      const building = new THREE.Mesh(
        new RoundedBoxGeometry(4.2, 4.4, 4.6, 4, 0.12),
        new THREE.MeshStandardMaterial({ color: 0xf0dfc8, roughness: 0.82 }),
      );
      building.position.set(11.55, 2.2, z);
      building.castShadow = false;
      group.add(building);
      const storefront = new THREE.Mesh(
        new RoundedBoxGeometry(0.12, 2.7, 3.15, 3, 0.05),
        new THREE.MeshStandardMaterial({ color: shopColors[i], roughness: 0.58 }),
      );
      storefront.position.set(9.41, 1.62, z);
      group.add(storefront);
      const window = new THREE.Mesh(
        new THREE.PlaneGeometry(2.15, 1.45),
        new THREE.MeshPhysicalMaterial({ color: 0x82b9bd, roughness: 0.16, metalness: 0.05, clearcoat: 0.55 }),
      );
      window.rotation.y = -Math.PI / 2;
      window.position.set(9.33, 1.48, z);
      group.add(window);
      const awning = new THREE.Mesh(
        new RoundedBoxGeometry(0.85, 0.22, 3.45, 3, 0.06),
        new THREE.MeshStandardMaterial({ color: i % 2 ? COLORS.teal : COLORS.coral, roughness: 0.5 }),
      );
      awning.position.set(9.09, 2.75, z);
      awning.rotation.z = -0.18;
      group.add(awning);
      const shopSign = this.createLabel(shopNames[i], 360, 96, i % 2 ? COLORS.tealDark : COLORS.coralDark, 38);
      shopSign.position.set(8.72, 3.52, z);
      shopSign.scale.set(1.5, 0.52, 1);
      group.add(shopSign);
    }

    const poleMat = new THREE.MeshStandardMaterial({ color: 0x2f4147, metalness: 0.62, roughness: 0.45 });
    for (const z of [-4.7, 1.2, 7.1]) {
      const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.11, 3.4, 10), poleMat);
      pole.position.set(8.43, 1.72, z);
      group.add(pole);
      const lamp = new THREE.Mesh(
        new THREE.SphereGeometry(0.24, 12, 8),
        new THREE.MeshStandardMaterial({ color: 0xffe3a2, emissive: 0xffc85b, emissiveIntensity: 0.85, roughness: 0.34 }),
      );
      lamp.position.set(8.43, 3.42, z);
      group.add(lamp);
      const arm = new THREE.Mesh(new THREE.TorusGeometry(0.34, 0.045, 6, 18, Math.PI), poleMat);
      arm.rotation.z = Math.PI / 2;
      arm.position.set(8.69, 3.26, z);
      group.add(arm);
    }
    return group;
  }

  private createChickenCrates(): { station: THREE.Group } {
    const station = new THREE.Group();
    station.add(this.createStorageRack(2.35, 3.25, [0.35, 1.18, 2.02, 3.0]));
    const boxMaterial = new THREE.MeshPhysicalMaterial({ color: 0xed4b17, roughness: 0.48, clearcoat: 0.24 });
    const lidMaterial = new THREE.MeshStandardMaterial({ color: 0xfff1de, roughness: 0.62 });
    const positions: Array<[number, number]> = [
      [-0.52, 0.68],
      [0.52, 0.68],
      [-0.52, 1.51],
      [0.52, 1.51],
    ];
    const cartonPanel = this.createPackagePanel('دجاج', 0.66, 0.24, '#fff1de', '#d9341f');
    const cartonBodies = new THREE.InstancedMesh(new RoundedBoxGeometry(0.92, 0.46, 0.82, 3, 0.06), boxMaterial, positions.length);
    const cartonLids = new THREE.InstancedMesh(new RoundedBoxGeometry(0.88, 0.055, 0.78, 2, 0.025), lidMaterial, positions.length);
    const cartonMatrix = new THREE.Matrix4();
    positions.forEach(([x, y], index) => {
      cartonBodies.setMatrixAt(index, cartonMatrix.makeTranslation(x, y, 0.03));
      cartonLids.setMatrixAt(index, cartonMatrix.makeTranslation(x, y + 0.24, 0.03));
      const panel = cartonPanel.clone();
      panel.position.set(x, y, 0.445);
      station.add(panel);
    });
    cartonBodies.instanceMatrix.needsUpdate = true;
    cartonLids.instanceMatrix.needsUpdate = true;
    station.add(cartonBodies, cartonLids);
    const bagBacking = new THREE.Mesh(
      new THREE.CapsuleGeometry(0.32, 0.5, 6, 12),
      new THREE.MeshPhysicalMaterial({ color: 0xe94719, roughness: 0.42, clearcoat: 0.36 }),
    );
    bagBacking.scale.set(0.82, 1.1, 0.38);
    bagBacking.position.set(0, 2.52, 0);
    station.add(bagBacking);
    const chickenBag = this.createAssetSprite(ASSET_PATHS.chicken, 0.72, 1.12, { x: 0.78, y: 0, width: 0.22, height: 1 });
    chickenBag.position.set(0, 2.53, 0.37);
    station.add(chickenBag);
    return { station };
  }

  private createChickenBoard(): { station: THREE.Group; crate: THREE.Group; rawChicken: THREE.Group; cutChicken: THREE.Group } {
    const station = this.createPrepTable(COLORS.coralDark);
    const board = new THREE.Mesh(new RoundedBoxGeometry(1.65, 0.12, 1.22, 4, 0.08), new THREE.MeshStandardMaterial({ color: COLORS.wood, roughness: 0.76 }));
    board.position.set(0, 1.45, 0);
    station.add(board);

    const crate = new THREE.Group();
    const crateBody = new THREE.Mesh(new RoundedBoxGeometry(1.4, 0.42, 0.95, 3, 0.08), new THREE.MeshStandardMaterial({ color: 0xe7a34e, roughness: 0.7 }));
    crate.add(crateBody);
    crate.position.set(0, 1.72, 0);
    crate.visible = false;
    station.add(crate);

    const rawChicken = this.createChicken(false, false);
    rawChicken.position.set(0, 1.67, 0);
    rawChicken.visible = false;
    station.add(rawChicken);

    const cutChicken = new THREE.Group();
    const left = this.createChickenHalf(false);
    const right = this.createChickenHalf(false);
    left.position.x = -0.38;
    right.position.x = 0.38;
    right.rotation.y = Math.PI;
    cutChicken.add(left, right);
    cutChicken.position.set(0, 1.63, 0);
    cutChicken.visible = false;
    station.add(cutChicken);

    const knife = new THREE.Group();
    const blade = new THREE.Mesh(new RoundedBoxGeometry(0.08, 0.62, 0.18, 2, 0.03), new THREE.MeshStandardMaterial({ color: COLORS.steel, metalness: 0.88, roughness: 0.25 }));
    blade.rotation.z = -0.35;
    const handle = new THREE.Mesh(new THREE.CapsuleGeometry(0.09, 0.34, 4, 8), new THREE.MeshStandardMaterial({ color: COLORS.tealDark, roughness: 0.55 }));
    handle.position.set(-0.18, 0.43, 0);
    handle.rotation.z = -0.35;
    knife.add(blade, handle);
    knife.position.set(0.62, 2.1, 0.42);
    station.add(knife);
    return { station, crate, rawChicken, cutChicken };
  }

  private createMarinadeStation(): { station: THREE.Group; chicken: THREE.Group } {
    const station = this.createPrepTable(COLORS.tealDark);
    const bowl = new THREE.Mesh(
      new THREE.CylinderGeometry(0.9, 0.68, 0.48, 32, 1, true),
      new THREE.MeshPhysicalMaterial({ color: 0xe8eef0, metalness: 0.74, roughness: 0.27, side: THREE.DoubleSide }),
    );
    bowl.position.y = 1.63;
    station.add(bowl);
    const sauce = new THREE.Mesh(new THREE.CylinderGeometry(0.72, 0.72, 0.1, 32), new THREE.MeshPhysicalMaterial({ color: COLORS.sauce, roughness: 0.25, clearcoat: 0.45 }));
    sauce.position.y = 1.53;
    station.add(sauce);
    const chicken = this.createChicken(false, true);
    chicken.scale.setScalar(0.72);
    chicken.position.y = 1.78;
    chicken.visible = false;
    station.add(chicken);
    return { station, chicken };
  }

  private createFridge(): { station: THREE.Group; chicken: THREE.Group } {
    const station = new THREE.Group();
    const bodyMat = new THREE.MeshPhysicalMaterial({ color: COLORS.cream, roughness: 0.32, clearcoat: 0.42 });
    const body = new THREE.Mesh(new RoundedBoxGeometry(2.0, 3.8, 1.7, 5, 0.16), bodyMat);
    body.position.y = 1.92;
    station.add(body);
    const split = new THREE.Mesh(new THREE.BoxGeometry(1.72, 0.05, 0.04), new THREE.MeshStandardMaterial({ color: COLORS.tealDark, roughness: 0.45 }));
    split.position.set(0, 1.42, 0.87);
    station.add(split);
    const handle = new THREE.Mesh(new THREE.CapsuleGeometry(0.065, 0.76, 4, 8), new THREE.MeshStandardMaterial({ color: COLORS.coral, roughness: 0.5 }));
    handle.position.set(0.7, 2.45, 0.94);
    station.add(handle);
    const badge = new THREE.Mesh(new THREE.CircleGeometry(0.25, 24), new THREE.MeshStandardMaterial({ color: COLORS.teal, emissive: COLORS.teal, emissiveIntensity: 0.6 }));
    badge.position.set(-0.58, 2.55, 0.88);
    station.add(badge);
    const chicken = this.createChicken(false, true);
    chicken.scale.setScalar(0.46);
    chicken.position.set(0, 1.1, 0.94);
    chicken.visible = false;
    station.add(chicken);
    return { station, chicken };
  }

  private createRiceBags(): { station: THREE.Group } {
    const station = new THREE.Group();
    const wood = new THREE.MeshStandardMaterial({ color: 0x85552f, roughness: 0.84 });
    for (const x of [-0.82, -0.28, 0.28, 0.82]) {
      const slat = new THREE.Mesh(new RoundedBoxGeometry(0.42, 0.16, 1.42, 2, 0.025), wood);
      slat.position.set(x, 0.12, 0);
      station.add(slat);
    }
    const sackMaterial = new THREE.MeshPhysicalMaterial({ color: 0xcda966, roughness: 0.88, sheen: 0.18, sheenColor: new THREE.Color(0xfff2cf) });
    const stitchMaterial = new THREE.MeshStandardMaterial({ color: COLORS.tealDark, roughness: 0.7 });
    const positions: Array<[number, number]> = [
      [-0.52, 0.5],
      [0.52, 0.5],
      [-0.52, 0.92],
      [0.52, 0.92],
      [0, 1.34],
    ];
    const ricePanel = this.createPackagePanel('رز', 0.64, 0.22, '#ead29b', '#087b7c');
    const sacks = new THREE.InstancedMesh(new RoundedBoxGeometry(1.0, 0.38, 0.82, 4, 0.14), sackMaterial, positions.length);
    const seams = new THREE.InstancedMesh(new RoundedBoxGeometry(0.82, 0.045, 0.86, 2, 0.02), stitchMaterial, positions.length);
    const sackMatrix = new THREE.Matrix4();
    positions.forEach(([x, y], index) => {
      sacks.setMatrixAt(index, sackMatrix.makeTranslation(x, y, 0));
      seams.setMatrixAt(index, sackMatrix.makeTranslation(x, y + 0.17, 0));
      const panel = ricePanel.clone();
      panel.position.set(x, y + 0.01, 0.425);
      station.add(panel);
    });
    sacks.instanceMatrix.needsUpdate = true;
    seams.instanceMatrix.needsUpdate = true;
    station.add(sacks, seams);
    const sideRail = new THREE.Mesh(new RoundedBoxGeometry(2.35, 0.1, 0.1, 2, 0.025), new THREE.MeshStandardMaterial({ color: COLORS.coralDark, roughness: 0.62 }));
    sideRail.position.set(0, 1.72, -0.46);
    station.add(sideRail);
    for (const x of [-1.1, 1.1]) {
      const post = new THREE.Mesh(new RoundedBoxGeometry(0.1, 1.62, 0.1, 2, 0.025), new THREE.MeshStandardMaterial({ color: COLORS.coralDark, roughness: 0.62 }));
      post.position.set(x, 0.9, -0.46);
      station.add(post);
    }
    return { station };
  }

  private createSink(): { station: THREE.Group; riceBag: THREE.Group; riceBowl: THREE.Group; water: THREE.Mesh } {
    const station = this.createPrepTable(COLORS.tealDark, 2.2);
    const sink = new THREE.Mesh(
      new RoundedBoxGeometry(1.65, 0.36, 1.08, 4, 0.16),
      new THREE.MeshPhysicalMaterial({ color: COLORS.steel, metalness: 0.88, roughness: 0.2 }),
    );
    sink.position.y = 1.42;
    station.add(sink);
    const basin = new THREE.Mesh(new RoundedBoxGeometry(1.34, 0.2, 0.78, 4, 0.14), new THREE.MeshStandardMaterial({ color: 0x65747a, metalness: 0.75, roughness: 0.3 }));
    basin.position.y = 1.59;
    station.add(basin);
    const faucet = new THREE.Group();
    const stem = new THREE.Mesh(new THREE.TubeGeometry(new THREE.QuadraticBezierCurve3(new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, 0.9, 0), new THREE.Vector3(0.62, 0.88, 0)), 16, 0.07, 8, false), new THREE.MeshStandardMaterial({ color: COLORS.steel, metalness: 0.9, roughness: 0.18 }));
    faucet.add(stem);
    faucet.position.set(-0.6, 1.66, -0.34);
    station.add(faucet);

    const riceBag = new THREE.Group();
    const bag = this.createAssetSprite(ASSET_PATHS.rice, 1.2, 0.68, { x: 0, y: 0, width: 0.38, height: 1 });
    riceBag.add(bag);
    riceBag.position.set(-0.72, 2.05, 0.2);
    riceBag.visible = false;
    station.add(riceBag);

    const riceBowl = new THREE.Group();
    const bowl = new THREE.Mesh(new THREE.CylinderGeometry(0.52, 0.42, 0.28, 24), new THREE.MeshStandardMaterial({ color: COLORS.cream, roughness: 0.42 }));
    const rice = new THREE.Mesh(new THREE.CylinderGeometry(0.44, 0.44, 0.07, 24), new THREE.MeshStandardMaterial({ color: 0xfff1c7, roughness: 0.88 }));
    rice.position.y = 0.18;
    riceBowl.add(bowl, rice);
    riceBowl.position.set(0.25, 1.75, 0.1);
    riceBowl.visible = false;
    station.add(riceBowl);
    const water = new THREE.Mesh(new THREE.CylinderGeometry(0.45, 0.45, 0.08, 24), new THREE.MeshPhysicalMaterial({ color: 0x6ed5ea, transparent: true, opacity: 0, roughness: 0.05, transmission: 0.25 }));
    water.position.set(0.25, 1.98, 0.1);
    water.visible = false;
    station.add(water);
    return { station, riceBag, riceBowl, water };
  }

  private createVegetableBasket(): { station: THREE.Group } {
    const station = this.createPrepTable(COLORS.coralDark, 3.1);
    const crateMaterial = new THREE.MeshStandardMaterial({ color: 0x85542f, roughness: 0.84 });
    const crateCenters = [-0.98, 0, 0.98];
    const kinds: Array<'onion' | 'potato' | 'tomato'> = ['onion', 'potato', 'tomato'];
    const produceLabel = this.createLabel('بصل  •  بطاطس  •  طماطم', 460, 72, COLORS.tealDark, 32);
    produceLabel.position.set(0, 1.48, 0.64);
    produceLabel.scale.set(2.1, 0.3, 1);
    station.add(produceLabel);
    const crates = new THREE.InstancedMesh(
      new RoundedBoxGeometry(0.88, 0.42, 1.12, 3, 0.07),
      crateMaterial,
      crateCenters.length,
    );
    const rails = new THREE.InstancedMesh(
      new RoundedBoxGeometry(0.08, 0.48, 1.18, 2, 0.02),
      crateMaterial,
      crateCenters.length * 2,
    );
    const crateMatrix = new THREE.Matrix4();
    let railIndex = 0;
    kinds.forEach((kind, index) => {
      const x = crateCenters[index];
      crates.setMatrixAt(index, crateMatrix.makeTranslation(x, 1.62, 0));
      for (const side of [-1, 1]) {
        rails.setMatrixAt(railIndex, crateMatrix.makeTranslation(x + side * 0.4, 1.83, 0));
        railIndex += 1;
      }
      station.add(this.createVegetableCrateFill(kind, x));
    });
    crates.instanceMatrix.needsUpdate = true;
    rails.instanceMatrix.needsUpdate = true;
    station.add(crates, rails);
    return { station };
  }

  private createVegetableBoard(): { station: THREE.Group; vegetables: THREE.Group; cuts: THREE.Group } {
    const station = this.createPrepTable(COLORS.coral);
    const board = new THREE.Mesh(new RoundedBoxGeometry(1.7, 0.12, 1.18, 4, 0.08), new THREE.MeshStandardMaterial({ color: COLORS.wood, roughness: 0.76 }));
    board.position.y = 1.45;
    station.add(board);
    const vegetables = this.createVegetableSet(false);
    vegetables.position.y = 1.72;
    vegetables.visible = false;
    station.add(vegetables);
    const cuts = this.createVegetableSet(true);
    cuts.position.y = 1.72;
    cuts.visible = false;
    station.add(cuts);
    return { station, vegetables, cuts };
  }

  private createOven(): {
    station: THREE.Group;
    body: THREE.Group;
    door: THREE.Group;
    needle: THREE.Group;
    glow: THREE.Mesh;
    steam: THREE.Group;
    platter: THREE.Group;
  } {
    const station = new THREE.Group();
    const body = new THREE.Group();
    station.add(body);

    const steel = new THREE.MeshPhysicalMaterial({ color: 0xc8d0d1, metalness: 0.92, roughness: 0.24, clearcoat: 0.28, envMapIntensity: 1.55 });
    const brightSteel = new THREE.MeshPhysicalMaterial({ color: 0xe1e6e6, metalness: 0.94, roughness: 0.18, clearcoat: 0.35 });
    const darkSteel = new THREE.MeshStandardMaterial({ color: 0x4b5558, metalness: 0.84, roughness: 0.32 });
    const black = new THREE.MeshStandardMaterial({ color: 0x211d1b, roughness: 0.82 });

    // The silhouette follows the supplied commercial mandi-oven reference:
    // a wide stainless cabinet, open trolley base, locking rails and top flue.
    const shell = new THREE.Mesh(new RoundedBoxGeometry(3.35, 3.95, 2.6, 5, 0.12), steel);
    shell.position.y = 3.05;
    body.add(shell);

    const lowerHopper = new THREE.Mesh(new THREE.CylinderGeometry(1.05, 0.78, 0.8, 4), darkSteel);
    lowerHopper.rotation.y = Math.PI / 4;
    lowerHopper.position.set(0, 0.96, 0.05);
    body.add(lowerHopper);

    for (const x of [-1.32, 1.32]) {
      for (const z of [-0.92, 0.92]) {
        const leg = new THREE.Mesh(new THREE.BoxGeometry(0.17, 1.05, 0.17), darkSteel);
        leg.position.set(x, 0.59, z);
        body.add(leg);
        const fork = new THREE.Mesh(new THREE.BoxGeometry(0.23, 0.28, 0.13), darkSteel);
        fork.position.set(x, 0.13, z);
        body.add(fork);
        const wheel = new THREE.Mesh(new THREE.TorusGeometry(0.19, 0.065, 8, 18), black);
        wheel.rotation.y = Math.PI / 2;
        wheel.position.set(x, 0.12, z + 0.04);
        body.add(wheel);
      }
    }

    const vent = new THREE.Mesh(new THREE.CylinderGeometry(0.24, 0.31, 0.78, 18), steel);
    vent.position.set(0.66, 5.38, 0.03);
    body.add(vent);
    const ventCap = new THREE.Mesh(new THREE.CylinderGeometry(0.43, 0.3, 0.14, 18), brightSteel);
    ventCap.position.set(0.66, 5.83, 0.03);
    body.add(ventCap);

    const cavity = new THREE.Mesh(new RoundedBoxGeometry(2.92, 3.48, 0.18, 4, 0.08), black);
    cavity.position.set(0, 3.08, 1.34);
    body.add(cavity);
    const glow = new THREE.Mesh(
      new RoundedBoxGeometry(2.72, 3.28, 0.07, 4, 0.06),
      new THREE.MeshStandardMaterial({ color: 0xff7b37, emissive: 0xff3b13, emissiveIntensity: 0.1, transparent: true, opacity: 0.25 }),
    );
    glow.position.set(0, 3.08, 1.46);
    body.add(glow);

    for (let i = 0; i < 3; i += 1) {
      const rack = new THREE.Mesh(new THREE.BoxGeometry(2.55, 0.07, 1.75), darkSteel);
      rack.position.set(0, 2.0 + i * 0.88, 0.62);
      body.add(rack);
    }
    const pot = new THREE.Mesh(new THREE.CylinderGeometry(1.03, 0.82, 0.78, 28), new THREE.MeshStandardMaterial({ color: 0x747d7f, metalness: 0.86, roughness: 0.27 }));
    pot.position.set(0, 1.72, 0.78);
    body.add(pot);
    const potLip = new THREE.Mesh(new THREE.TorusGeometry(1.02, 0.065, 8, 28), brightSteel);
    potLip.rotation.x = Math.PI / 2;
    potLip.position.set(0, 2.1, 0.78);
    body.add(potLip);

    // Door pivots around the left-side hinge. Every rail and latch moves with it.
    const door = new THREE.Group();
    door.position.set(-1.53, 3.1, 1.48);
    door.rotation.y = -0.72;
    body.add(door);
    const doorPanel = new THREE.Mesh(new RoundedBoxGeometry(3.06, 3.62, 0.2, 5, 0.08), steel);
    doorPanel.position.x = 1.53;
    door.add(doorPanel);

    const railMat = new THREE.MeshStandardMaterial({ color: 0x7d898b, metalness: 0.91, roughness: 0.23 });
    for (const y of [-1.58, 1.58]) {
      const rail = new THREE.Mesh(new RoundedBoxGeometry(3.28, 0.13, 0.14, 3, 0.04), railMat);
      rail.position.set(1.53, y, 0.2);
      door.add(rail);
      for (const x of [0.08, 2.98]) {
        const cap = new THREE.Mesh(new THREE.CylinderGeometry(0.11, 0.11, 0.34, 12), darkSteel);
        cap.rotation.x = Math.PI / 2;
        cap.position.set(x, y, 0.23);
        door.add(cap);
      }
    }
    for (const x of [0.08, 2.98]) {
      const upright = new THREE.Mesh(new RoundedBoxGeometry(0.13, 3.25, 0.13, 3, 0.04), railMat);
      upright.position.set(x, 0, 0.2);
      door.add(upright);
    }

    const gaugePlate = new THREE.Mesh(new RoundedBoxGeometry(0.76, 0.76, 0.1, 4, 0.06), brightSteel);
    gaugePlate.position.set(1.53, 0.12, 0.2);
    door.add(gaugePlate);
    const gaugeFace = new THREE.Mesh(new THREE.CircleGeometry(0.25, 28), new THREE.MeshStandardMaterial({ color: 0xf7f2e8, roughness: 0.42 }));
    gaugeFace.position.set(1.53, 0.12, 0.27);
    door.add(gaugeFace);
    const needle = new THREE.Group();
    needle.position.set(1.53, 0.12, 0.3);
    needle.rotation.z = -1.15;
    const needleLine = new THREE.Mesh(new RoundedBoxGeometry(0.25, 0.035, 0.025, 2, 0.01), new THREE.MeshBasicMaterial({ color: COLORS.coralDark }));
    needleLine.position.x = 0.1;
    needle.add(needleLine);
    door.add(needle);

    const latch = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.78, 0.2), darkSteel);
    latch.position.set(2.58, 0.12, 0.3);
    door.add(latch);
    const latchGrip = new THREE.Mesh(new THREE.CapsuleGeometry(0.075, 0.5, 4, 8), brightSteel);
    latchGrip.position.set(2.8, 0.12, 0.42);
    latchGrip.rotation.z = Math.PI / 2;
    door.add(latchGrip);

    for (const y of [-1.32, 1.32]) {
      const hinge = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.1, 0.5, 12), darkSteel);
      hinge.position.set(0, y, 0.22);
      door.add(hinge);
    }

    const platter = this.createPlatter();
    platter.position.set(0, 1.58, 2.42);
    platter.scale.setScalar(0.78);
    platter.visible = false;
    body.add(platter);

    const steam = new THREE.Group();
    steam.position.x = 0.66;
    for (let i = 0; i < 6; i += 1) {
      const puff = new THREE.Mesh(new THREE.SphereGeometry(0.2 + i * 0.024, 10, 8), new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.18, depthWrite: false }));
      puff.scale.set(0.82, 1.65, 0.82);
      puff.position.set(0, 5.25 + i * 0.23, 0);
      steam.add(puff);
    }
    steam.visible = false;
    body.add(steam);
    // Keep the oven recognisable and animated without letting it dominate the
    // whole back wall. Internal animation values remain in the authored scale.
    body.scale.setScalar(0.72);
    return { station, body, door, needle, glow, steam, platter };
  }

  private createChef(): THREE.Group {
    const chef = new THREE.Group();
    chef.name = 'kitchen-chef';
    const skin = new THREE.MeshStandardMaterial({ color: 0xc9855f, roughness: 0.7 });
    const uniform = new THREE.MeshPhysicalMaterial({ color: 0xf7efe2, roughness: 0.5, sheen: 0.32, sheenColor: new THREE.Color(0xffffff) });
    const apron = new THREE.MeshStandardMaterial({ color: COLORS.teal, roughness: 0.56 });
    const pants = new THREE.MeshStandardMaterial({ color: 0x343f42, roughness: 0.74 });

    const torso = new THREE.Mesh(new THREE.CapsuleGeometry(0.34, 0.68, 7, 14), uniform);
    torso.position.y = 1.55;
    chef.add(torso);
    const apronPanel = new THREE.Mesh(new RoundedBoxGeometry(0.56, 0.72, 0.08, 3, 0.04), apron);
    apronPanel.position.set(0, 1.5, 0.31);
    chef.add(apronPanel);

    const head = new THREE.Mesh(new THREE.SphereGeometry(0.32, 18, 14), skin);
    head.position.y = 2.45;
    chef.add(head);
    const hatBand = new THREE.Mesh(new THREE.CylinderGeometry(0.35, 0.35, 0.22, 18), uniform);
    hatBand.position.y = 2.75;
    chef.add(hatBand);
    for (const x of [-0.23, 0, 0.23]) {
      const puff = new THREE.Mesh(new THREE.SphereGeometry(0.26, 14, 10), uniform);
      puff.position.set(x, 2.98 + (x === 0 ? 0.08 : 0), 0);
      chef.add(puff);
    }

    for (const side of [-1, 1]) {
      const leg = new THREE.Mesh(new THREE.CapsuleGeometry(0.105, 0.5, 6, 10), pants);
      leg.position.set(side * 0.19, 0.62, 0);
      chef.add(leg);
      const shoe = new THREE.Mesh(new RoundedBoxGeometry(0.24, 0.14, 0.4, 3, 0.05), new THREE.MeshStandardMaterial({ color: 0x262525, roughness: 0.82 }));
      shoe.position.set(side * 0.19, 0.17, 0.1);
      chef.add(shoe);

      const arm = new THREE.Group();
      arm.name = side < 0 ? 'chef-left-arm' : 'chef-right-arm';
      const sleeve = new THREE.Mesh(new THREE.CapsuleGeometry(0.1, 0.44, 6, 10), uniform);
      sleeve.position.y = -0.29;
      arm.add(sleeve);
      const hand = new THREE.Mesh(new THREE.SphereGeometry(0.12, 12, 9), skin);
      hand.position.y = -0.62;
      arm.add(hand);
      arm.position.set(side * 0.44, 1.95, 0.02);
      arm.rotation.z = side * 0.16;
      chef.add(arm);
    }

    return chef;
  }

  private createCourier(): { station: THREE.Group; arm: THREE.Group } {
    const station = new THREE.Group();
    const skin = new THREE.MeshStandardMaterial({ color: 0xb97850, roughness: 0.72 });
    const uniform = new THREE.MeshPhysicalMaterial({ color: COLORS.teal, roughness: 0.55, sheen: 0.35, sheenColor: new THREE.Color(0xffffff) });
    const pants = new THREE.MeshStandardMaterial({ color: 0x29363c, roughness: 0.75 });
    const torso = new THREE.Mesh(new THREE.CapsuleGeometry(0.34, 0.72, 8, 16), uniform);
    torso.position.y = 1.75;
    station.add(torso);
    const head = new THREE.Mesh(new THREE.SphereGeometry(0.34, 20, 14), skin);
    head.position.y = 2.72;
    station.add(head);
    const helmet = new THREE.Mesh(new THREE.SphereGeometry(0.39, 20, 10, 0, Math.PI * 2, 0, Math.PI * 0.58), new THREE.MeshPhysicalMaterial({ color: COLORS.coral, roughness: 0.28, clearcoat: 0.7 }));
    helmet.position.y = 2.83;
    station.add(helmet);
    for (const x of [-0.2, 0.2]) {
      const leg = new THREE.Mesh(new THREE.CapsuleGeometry(0.11, 0.58, 6, 10), pants);
      leg.position.set(x, 0.76, 0);
      station.add(leg);
    }
    const box = new THREE.Mesh(new RoundedBoxGeometry(1.2, 0.9, 0.76, 4, 0.12), new THREE.MeshStandardMaterial({ color: COLORS.cream, roughness: 0.58 }));
    box.position.set(0, 1.55, -0.65);
    station.add(box);
    const boxMark = this.createLabel('كايف', 200, 80, COLORS.coral, 44);
    boxMark.position.set(0, 1.58, -1.05);
    boxMark.scale.set(0.78, 0.32, 1);
    boxMark.rotation.y = Math.PI;
    station.add(boxMark);
    const arm = new THREE.Group();
    const sleeve = new THREE.Mesh(new THREE.CapsuleGeometry(0.11, 0.56, 6, 10), uniform);
    sleeve.position.y = -0.34;
    arm.add(sleeve);
    const hand = new THREE.Mesh(new THREE.SphereGeometry(0.14, 12, 10), skin);
    hand.position.y = -0.72;
    arm.add(hand);
    arm.position.set(-0.46, 2.18, 0.05);
    arm.rotation.z = -0.28;
    station.add(arm);
    const courierLabel = this.createLabel('مندوب التوصيل', 384, 104, COLORS.tealDark, 40);
    courierLabel.position.set(0, 3.55, 0);
    courierLabel.scale.set(2.65, 0.72, 1);
    station.add(courierLabel);
    return { station, arm };
  }

  private createAssetSprite(
    path: string,
    width: number,
    height: number,
    crop?: { x: number; y: number; width: number; height: number },
  ): THREE.Sprite {
    const texture = new THREE.TextureLoader().load(path);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.magFilter = THREE.LinearFilter;
    if (crop) {
      texture.wrapS = THREE.ClampToEdgeWrapping;
      texture.wrapT = THREE.ClampToEdgeWrapping;
      texture.offset.set(crop.x, crop.y);
      texture.repeat.set(crop.width, crop.height);
    }
    const material = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      alphaTest: 0.035,
      depthWrite: true,
      toneMapped: false,
    });
    const sprite = new THREE.Sprite(material);
    sprite.scale.set(width, height, 1);
    return sprite;
  }

  private createPackagePanel(text: string, width: number, height: number, background: string, foreground: string): THREE.Mesh {
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 96;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Canvas 2D unavailable for package decals.');
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = background;
    context.beginPath();
    context.roundRect(4, 4, 248, 88, 18);
    context.fill();
    context.strokeStyle = foreground;
    context.lineWidth = 5;
    context.stroke();
    context.fillStyle = foreground;
    context.font = '900 46px Tahoma, Arial, sans-serif';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.direction = 'rtl';
    context.fillText(text, 128, 49);
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    const material = new THREE.MeshBasicMaterial({ map: texture, transparent: true, toneMapped: false, side: THREE.DoubleSide });
    return new THREE.Mesh(new THREE.PlaneGeometry(width, height), material);
  }

  private createStorageRack(width: number, height: number, shelves: number[]): THREE.Group {
    const rack = new THREE.Group();
    const frameMaterial = new THREE.MeshStandardMaterial({ color: 0x315b5e, metalness: 0.35, roughness: 0.52 });
    const shelfMaterial = new THREE.MeshStandardMaterial({ color: 0xd9974d, roughness: 0.66 });
    for (const x of [-width / 2, width / 2]) {
      for (const z of [-0.48, 0.48]) {
        const post = new THREE.Mesh(new RoundedBoxGeometry(0.1, height, 0.1, 2, 0.025), frameMaterial);
        post.position.set(x, height / 2, z);
        rack.add(post);
      }
    }
    for (const y of shelves) {
      const shelf = new THREE.Mesh(new RoundedBoxGeometry(width + 0.18, 0.12, 1.12, 2, 0.035), shelfMaterial);
      shelf.position.y = y;
      rack.add(shelf);
    }
    const braceA = new THREE.Mesh(new THREE.BoxGeometry(0.08, height * 0.82, 0.08), frameMaterial);
    braceA.position.set(0, height / 2, -0.5);
    braceA.rotation.z = 0.58;
    rack.add(braceA);
    const braceB = braceA.clone();
    braceB.rotation.z = -0.58;
    rack.add(braceB);
    return rack;
  }

  private createPrepTable(accent: number, width = 2): THREE.Group {
    const group = new THREE.Group();
    const top = new THREE.Mesh(new RoundedBoxGeometry(width, 0.2, 1.5, 4, 0.07), new THREE.MeshStandardMaterial({ color: 0xe7a24f, roughness: 0.54 }));
    top.position.y = 1.32;
    group.add(top);
    const frameMat = new THREE.MeshStandardMaterial({ color: accent, metalness: 0.08, roughness: 0.58 });
    const cabinet = new THREE.Mesh(new RoundedBoxGeometry(width * 0.92, 1.16, 1.32, 4, 0.07), frameMat);
    cabinet.position.y = 0.65;
    group.add(cabinet);
    const drawer = new THREE.Mesh(new RoundedBoxGeometry(width * 0.76, 0.25, 0.08, 3, 0.03), new THREE.MeshStandardMaterial({ color: COLORS.cream, roughness: 0.55 }));
    drawer.position.set(0, 0.98, 0.69);
    group.add(drawer);
    const handle = new THREE.Mesh(new RoundedBoxGeometry(width * 0.32, 0.055, 0.06, 3, 0.02), new THREE.MeshStandardMaterial({ color: COLORS.steelDark, metalness: 0.58, roughness: 0.3 }));
    handle.position.set(0, 0.98, 0.75);
    group.add(handle);
    const toe = new THREE.Mesh(new THREE.BoxGeometry(width * 0.76, 0.16, 0.08), new THREE.MeshStandardMaterial({ color: 0x332a27, roughness: 0.78 }));
    toe.position.set(0, 0.14, 0.66);
    group.add(toe);
    return group;
  }

  private createChicken(cooked: boolean, sauced: boolean): THREE.Group {
    const chicken = new THREE.Group();
    const material = new THREE.MeshPhysicalMaterial({
      color: cooked ? 0xa72b1f : sauced ? 0xc8462f : 0xf0b58d,
      roughness: cooked ? 0.34 : 0.58,
      clearcoat: cooked || sauced ? 0.5 : 0.1,
      clearcoatRoughness: 0.24,
    });
    const breast = new THREE.Mesh(new THREE.SphereGeometry(0.48, 22, 15), material);
    breast.scale.set(1.15, 0.48, 0.82);
    chicken.add(breast);
    for (const side of [-1, 1]) {
      const leg = new THREE.Mesh(new THREE.CapsuleGeometry(0.13, 0.48, 6, 12), material);
      leg.position.set(side * 0.43, 0.02, 0.08);
      leg.rotation.z = side * 0.92;
      chicken.add(leg);
    }
    chicken.rotation.x = -0.08;
    return chicken;
  }

  private createChickenHalf(cooked: boolean): THREE.Group {
    const half = this.createChicken(cooked, !cooked);
    half.scale.set(0.65, 0.82, 0.72);
    return half;
  }

  private createVegetableSet(cut: boolean): THREE.Group {
    const group = new THREE.Group();
    const tomato = this.createVegetableProp('tomato', cut);
    const potato = this.createVegetableProp('potato', cut);
    const onion = this.createVegetableProp('onion', cut);
    tomato.position.set(-0.48, 0.18, 0.03);
    potato.position.set(0, 0.18, -0.04);
    onion.position.set(0.48, 0.18, 0.08);
    group.add(tomato, potato, onion);
    return group;
  }

  private createVegetableCrateFill(kind: 'onion' | 'potato' | 'tomato', centerX: number): THREE.Group {
    const fill = new THREE.Group();
    const bodyGeometry = new THREE.SphereGeometry(0.18, 12, 8);
    const bodyMaterial = new THREE.MeshPhysicalMaterial({
      color: kind === 'tomato' ? 0xe13d2f : kind === 'potato' ? 0xc99a53 : 0x9d234f,
      roughness: kind === 'potato' ? 0.84 : 0.46,
      clearcoat: kind === 'tomato' ? 0.28 : 0.08,
    });
    const bodies = new THREE.InstancedMesh(bodyGeometry, bodyMaterial, 6);
    const matrix = new THREE.Matrix4();
    const rotation = new THREE.Quaternion();
    const scale = new THREE.Vector3();
    for (let item = 0; item < 6; item += 1) {
      const position = new THREE.Vector3(
        centerX + (item % 2 === 0 ? -0.18 : 0.18),
        1.98 + (item % 3) * 0.025,
        -0.3 + Math.floor(item / 2) * 0.3,
      );
      rotation.setFromEuler(new THREE.Euler(0, (item % 3 - 1) * 0.22, kind === 'potato' ? 0.18 : 0));
      scale.set(kind === 'potato' ? 1.25 : 1, kind === 'potato' ? 0.78 : kind === 'onion' ? 1.08 : 0.88, kind === 'potato' ? 0.92 : 1);
      matrix.compose(position, rotation, scale);
      bodies.setMatrixAt(item, matrix);
    }
    bodies.instanceMatrix.needsUpdate = true;
    bodies.castShadow = true;
    fill.add(bodies);
    if (kind !== 'potato') {
      const accentGeometry = kind === 'tomato' ? new THREE.ConeGeometry(0.07, 0.17, 5) : new THREE.ConeGeometry(0.06, 0.16, 7);
      const accentMaterial = new THREE.MeshStandardMaterial({ color: kind === 'tomato' ? 0x497d38 : 0x8e1c47, roughness: 0.68 });
      const accents = new THREE.InstancedMesh(accentGeometry, accentMaterial, 6);
      for (let item = 0; item < 6; item += 1) {
        const position = new THREE.Vector3(
          centerX + (item % 2 === 0 ? -0.18 : 0.18),
          2.17 + (item % 3) * 0.025,
          -0.3 + Math.floor(item / 2) * 0.3,
        );
        matrix.compose(position, new THREE.Quaternion(), new THREE.Vector3(1, 1, 1));
        accents.setMatrixAt(item, matrix);
      }
      accents.instanceMatrix.needsUpdate = true;
      accents.castShadow = true;
      fill.add(accents);
    }
    return fill;
  }

  private createVegetableProp(kind: 'tomato' | 'potato' | 'onion', cut: boolean): THREE.Group {
    const group = new THREE.Group();
    const tomatoMaterial = new THREE.MeshPhysicalMaterial({ color: 0xe13d2f, roughness: 0.38, clearcoat: 0.32 });
    const potatoMaterial = new THREE.MeshStandardMaterial({ color: 0xc99a53, roughness: 0.84 });
    const potatoFlesh = new THREE.MeshStandardMaterial({ color: 0xf2d276, roughness: 0.72 });
    const onionSkin = new THREE.MeshPhysicalMaterial({ color: 0x9d234f, roughness: 0.48, clearcoat: 0.18 });
    const onionFlesh = new THREE.MeshStandardMaterial({ color: 0xf2d7e8, roughness: 0.7 });
    if (cut) {
      if (kind === 'potato') {
        for (let i = 0; i < 4; i += 1) {
          const slice = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.2, 0.085, 16), potatoFlesh);
          slice.position.set((i - 1.5) * 0.13, i * 0.025, (i % 2 ? 0.09 : -0.08));
          group.add(slice);
        }
        return group;
      }
      const material = kind === 'tomato' ? tomatoMaterial : onionFlesh;
      for (let i = 0; i < 6; i += 1) {
        const cube = new THREE.Mesh(new RoundedBoxGeometry(0.18, 0.12, 0.18, 2, 0.025), material);
        cube.position.set(-0.22 + (i % 3) * 0.22, Math.floor(i / 3) * 0.09, -0.1 + Math.floor(i / 3) * 0.2);
        group.add(cube);
        if (kind === 'onion' && i % 2 === 0) {
          const skin = new THREE.Mesh(new THREE.BoxGeometry(0.185, 0.02, 0.185), onionSkin);
          skin.position.set(cube.position.x, cube.position.y + 0.07, cube.position.z);
          group.add(skin);
        }
      }
      return group;
    }

    if (kind === 'tomato') {
      const fruit = new THREE.Mesh(new THREE.SphereGeometry(0.27, 18, 12), tomatoMaterial);
      fruit.scale.y = 0.86;
      group.add(fruit);
      const calyxMaterial = new THREE.MeshStandardMaterial({ color: 0x497d38, roughness: 0.66 });
      for (let i = 0; i < 5; i += 1) {
        const leaf = new THREE.Mesh(new THREE.ConeGeometry(0.07, 0.24, 5), calyxMaterial);
        leaf.position.set(Math.cos((i / 5) * Math.PI * 2) * 0.1, 0.23, Math.sin((i / 5) * Math.PI * 2) * 0.1);
        leaf.rotation.z = Math.PI / 2;
        leaf.rotation.y = (i / 5) * Math.PI * 2;
        group.add(leaf);
      }
      return group;
    }
    if (kind === 'potato') {
      const potato = new THREE.Mesh(new THREE.SphereGeometry(0.28, 16, 11), potatoMaterial);
      potato.scale.set(1.25, 0.78, 0.92);
      potato.rotation.z = 0.15;
      group.add(potato);
      const eyeMaterial = new THREE.MeshStandardMaterial({ color: 0x704827, roughness: 0.9 });
      for (const [x, y, z] of [[-0.15, 0.16, 0.18], [0.12, 0.13, 0.2], [0.02, 0.2, -0.1]] as Array<[number, number, number]>) {
        const eye = new THREE.Mesh(new THREE.SphereGeometry(0.022, 6, 4), eyeMaterial);
        eye.position.set(x, y, z);
        group.add(eye);
      }
      return group;
    }
    const onion = new THREE.Mesh(new THREE.SphereGeometry(0.27, 18, 12), onionSkin);
    onion.scale.y = 1.08;
    group.add(onion);
    const tip = new THREE.Mesh(new THREE.ConeGeometry(0.075, 0.22, 7), onionSkin);
    tip.position.y = 0.32;
    group.add(tip);
    const root = new THREE.Mesh(new THREE.CylinderGeometry(0.065, 0.09, 0.05, 10), new THREE.MeshStandardMaterial({ color: 0xd2b069, roughness: 0.86 }));
    root.position.y = -0.28;
    group.add(root);
    return group;
  }

  private createPlatter(): THREE.Group {
    const platter = new THREE.Group();
    const tray = new THREE.Mesh(new THREE.CylinderGeometry(1.35, 1.25, 0.16, 48), new THREE.MeshStandardMaterial({ color: 0xbfc7ca, metalness: 0.86, roughness: 0.28 }));
    platter.add(tray);
    const rice = new THREE.Mesh(new THREE.CylinderGeometry(1.18, 1.15, 0.22, 48), new THREE.MeshStandardMaterial({ color: COLORS.rice, roughness: 0.82 }));
    rice.position.y = 0.16;
    platter.add(rice);
    for (let i = 0; i < 6; i += 1) {
      const potato = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.2, 0.09, 16), new THREE.MeshStandardMaterial({ color: 0xd9a34f, roughness: 0.68 }));
      const angle = (i / 6) * Math.PI * 2;
      potato.position.set(Math.cos(angle) * 0.92, 0.34, Math.sin(angle) * 0.92);
      platter.add(potato);
    }
    const left = this.createChickenHalf(true);
    const right = this.createChickenHalf(true);
    left.position.set(-0.34, 0.5, 0);
    right.position.set(0.34, 0.5, 0);
    right.rotation.y = Math.PI;
    platter.add(left, right);
    return platter;
  }

  private createLabel(text: string, width = 320, height = 96, color = COLORS.tealDark, fontSize = 42): THREE.Sprite {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D unavailable for labels.');
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = 'rgba(255, 247, 233, 0.94)';
    ctx.strokeStyle = 'rgba(79, 54, 39, 0.18)';
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.roundRect(6, 6, width - 12, height - 12, 22);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = `#${color.toString(16).padStart(6, '0')}`;
    ctx.font = `800 ${fontSize}px Tahoma, Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.direction = 'rtl';
    ctx.fillText(text, width / 2, height / 2 + 2);
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: texture, transparent: true, depthWrite: false }));
    sprite.scale.set(2.2, 0.66, 1);
    return sprite;
  }

  private measure(): KitchenWorldDiagnostics {
    const geometries = new Set<THREE.BufferGeometry>();
    const materials = new Set<THREE.Material>();
    let meshes = 0;
    this.root.traverse((node) => {
      if (!(node instanceof THREE.Mesh)) return;
      meshes += 1;
      geometries.add(node.geometry);
      if (Array.isArray(node.material)) node.material.forEach((material) => materials.add(material));
      else materials.add(node.material);
    });
    return { stationCount: this.stations.size, meshes, materials: materials.size, geometries: geometries.size };
  }
}

